import React, { useEffect, useState } from "react";
import { Avatar } from "@/components/Avatar";
import { api, formatError, getToken, API } from "@/lib/api";
import { toast } from "sonner";
import { MapPin, Sparkles, Send, Copy, Ban, Star, MessageCircle, Lock, Eye } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";

/**
 * Compact profile dialog for ANY user in the app.
 * Props:
 *   open, onClose, userId, seed (optional — a partial user object for instant render while fetching)
 *   onBlocked(userId) — called after successful block so the caller can drop their messages
 */
export default function ProfileCard({ open, onClose, userId, seed, onBlocked }) {
  const { user: me } = useAuth();
  const nav = useNavigate();
  const [user, setUser] = useState(seed || null);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [favorited, setFavorited] = useState(false);
  const [favBusy, setFavBusy] = useState(false);
  const [dmBusy, setDmBusy] = useState(false);
  const [priv, setPriv] = useState({ status: "none", images: [] });
  const [privBusy, setPrivBusy] = useState(false);

  useEffect(() => {
    if (!open || !userId) return;
    let active = true;
    setLoading(true);
    api.get(`/users/${userId}`)
      .then(({ data }) => { if (active) setUser(data); })
      .catch(() => { /* fallback to seed */ })
      .finally(() => { if (active) setLoading(false); });
    // Load my favorites to know if this user is starred
    api.get("/friends")
      .then(({ data }) => {
        if (!active) return;
        setFavorited((data || []).some((f) => f.id === userId));
      })
      .catch(() => {});
    // Load private-album status (if approved, returns images)
    api.get(`/users/${userId}/private-images`)
      .then(({ data }) => { if (active) setPriv(data); })
      .catch(() => {});
    return () => { active = false; };
  }, [open, userId]);

  const isSelf = user?.id === me?.id;
  // Masked profiles (from DownLow recent posters) have a "masked:<realId>" id and no real userId to fetch
  const isMasked = !!user?.masked;

  const toggleFavorite = async () => {
    if (!user) return;
    setFavBusy(true);
    try {
      if (favorited) {
        await api.delete(`/friends/${user.id}`);
        setFavorited(false);
        toast.success("Removed from favorites");
      } else {
        await api.post(`/friends/${user.id}`);
        setFavorited(true);
        toast.success(`@${user.username} added to favorites`);
      }
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setFavBusy(false);
    }
  };

  const startDm = async () => {
    if (!user) return;
    setDmBusy(true);
    try {
      const { data } = await api.post(`/dms/${user.id}`);
      onClose?.();
      nav(`/room/${data.id}`);
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setDmBusy(false);
    }
  };

  const requestPrivate = async () => {
    if (!user) return;
    setPrivBusy(true);
    try {
      const { data } = await api.post(`/users/${user.id}/private-access/request`);
      setPriv((prev) => ({ ...prev, status: data.status }));
      toast.success(data.status === "approved" ? "Already approved — loading pics…" : "Request sent. They'll get a notification.");
      if (data.status === "approved") {
        const r = await api.get(`/users/${user.id}/private-images`);
        setPriv(r.data);
      }
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setPrivBusy(false);
    }
  };

  const createInvite = async () => {
    setCreating(true);
    try {
      const { data } = await api.post("/rooms/mini", {
        name: `with @${user.username}`,
        description: `Private room for you and @${user.username}.`,
        topic: "Invite",
      });
      const url = `${window.location.origin}/invite/${data.invite_code}`;
      try { await navigator.clipboard.writeText(url); } catch {}
      toast.success("Invite link copied — send it to them");
      onClose?.();
      nav(`/room/${data.id}`);
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setCreating(false);
    }
  };

  const block = async () => {
    try {
      await api.post(`/users/${user.id}/block`);
      toast.success(`Blocked ${user.username}`);
      onBlocked?.(user.id);
      onClose?.();
    } catch (err) {
      toast.error(formatError(err));
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="bg-[#0b0b0e] border-white/10 text-white max-w-sm">
        <DialogHeader>
          <DialogTitle className="sr-only">{user?.username || "Profile"}</DialogTitle>
        </DialogHeader>
        {!user ? (
          <div className="py-10 text-center text-zinc-500 text-sm">{loading ? "Loading…" : "No profile"}</div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <Avatar user={user} size={72} />
              <div className="min-w-0">
                <div className="font-display text-2xl font-black tracking-tight truncate flex items-center gap-2" data-testid="profile-card-username">
                  {user.username}
                  {isMasked && (
                    <span className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-white/5 border border-white/10 text-zinc-300">
                      masked
                    </span>
                  )}
                </div>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-zinc-400 mt-1">
                  {user.pronouns && <span>{user.pronouns}</span>}
                  {user.location && (
                    <span className="inline-flex items-center gap-1" data-testid="profile-card-location">
                      <MapPin className="w-3 h-3 text-fuchsia-400" /> {user.location}
                    </span>
                  )}
                  {user.age && <span>· {user.age}</span>}
                </div>
                {user.role === "admin" && (
                  <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-fuchsia-300 mt-1">
                    <Sparkles className="w-3 h-3" /> admin
                  </span>
                )}
              </div>
            </div>

            {user.bio && (
              <div className="text-sm text-zinc-300 leading-relaxed whitespace-pre-wrap border-t border-white/5 pt-4">
                {user.bio}
              </div>
            )}

            {/* Private album */}
            {!isSelf && !isMasked && (
              <div className="border-t border-white/5 pt-4">
                <div className="flex items-center gap-2 mb-2 text-xs">
                  <Lock className="w-3.5 h-3.5 text-fuchsia-400" />
                  <span className="font-semibold tracking-wider uppercase text-zinc-300">Private album</span>
                </div>
                {priv.status === "approved" && (priv.images || []).length > 0 ? (
                  <div className="grid grid-cols-2 gap-2" data-testid="profile-card-private-images">
                    {priv.images.map((p) => (
                      <PrivateThumb key={p.id} pic={p} />
                    ))}
                  </div>
                ) : priv.status === "approved" ? (
                  <div className="text-xs text-zinc-500">They haven't added any private pics yet.</div>
                ) : priv.status === "pending" ? (
                  <div className="text-xs text-zinc-400 inline-flex items-center gap-1.5 rounded-full bg-white/5 border border-white/10 px-3 py-1.5" data-testid="private-status-pending">
                    <Eye className="w-3 h-3" /> Request pending
                  </div>
                ) : priv.status === "denied" ? (
                  <div className="text-xs text-rose-300/80">Your request was declined.</div>
                ) : (
                  <button
                    onClick={requestPrivate}
                    disabled={privBusy}
                    data-testid="request-private-btn"
                    className="text-xs px-3 py-1.5 rounded-full bg-fuchsia-500/10 border border-fuchsia-500/30 text-fuchsia-200 hover:bg-fuchsia-500/20 inline-flex items-center gap-1.5 transition"
                  >
                    <Eye className="w-3 h-3" /> {privBusy ? "Sending…" : "Request to see private pics"}
                  </button>
                )}
              </div>
            )}

            {isMasked && (
              <div className="text-xs text-zinc-500 text-center border-t border-white/5 pt-3">
                This person is incognito in DownLow. Identity hidden by design.
              </div>
            )}

            {!isSelf && !isMasked && (
              <div className="flex flex-col gap-2 pt-2">
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={startDm}
                    disabled={dmBusy}
                    data-testid="profile-message-btn"
                    className="neon-btn inline-flex items-center justify-center gap-2 text-sm py-2.5"
                  >
                    <MessageCircle className="w-4 h-4" /> {dmBusy ? "Opening…" : "Message"}
                  </button>
                  <button
                    onClick={toggleFavorite}
                    disabled={favBusy}
                    data-testid="profile-favorite-btn"
                    className={`text-sm px-4 py-2.5 rounded-full inline-flex items-center justify-center gap-2 transition border ${
                      favorited
                        ? "bg-amber-400/10 border-amber-400/40 text-amber-200"
                        : "bg-white/5 border-white/10 text-zinc-200 hover:bg-white/10"
                    }`}
                  >
                    <Star className={`w-4 h-4 ${favorited ? "fill-amber-400 text-amber-400" : ""}`} />
                    {favorited ? "Favorited" : "Favorite"}
                  </button>
                </div>
                <button
                  onClick={createInvite}
                  disabled={creating}
                  data-testid="invite-to-mini-btn"
                  className="ghost-btn w-full inline-flex items-center justify-center gap-2 text-sm"
                >
                  <Send className="w-4 h-4" /> {creating ? "Creating room…" : "Invite to a mini room"}
                </button>
                <div className="flex gap-2">
                  <button
                    onClick={async () => {
                      try {
                        await navigator.clipboard.writeText(`@${user.username}`);
                        toast.success("Handle copied");
                      } catch {}
                    }}
                    className="flex-1 ghost-btn inline-flex items-center justify-center gap-2 text-sm"
                  >
                    <Copy className="w-3.5 h-3.5" /> Copy handle
                  </button>
                  <button
                    onClick={block}
                    data-testid="profile-card-block-btn"
                    className="flex-1 text-sm px-4 py-2 rounded-full bg-rose-500/10 border border-rose-500/30 text-rose-300 hover:bg-rose-500/20 inline-flex items-center justify-center gap-2"
                  >
                    <Ban className="w-3.5 h-3.5" /> Block
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function PrivateThumb({ pic }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [expanded, setExpanded] = useState(false);
  useEffect(() => {
    const token = getToken();
    let url = null;
    let cancelled = false;
    fetch(`${API}/files/${pic.storage_path}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then(async (r) => {
        if (!r.ok) throw new Error("fetch failed");
        const blob = await r.blob();
        if (cancelled) return;
        url = URL.createObjectURL(blob);
        setBlobUrl(url);
      })
      .catch(() => {});
    return () => { cancelled = true; if (url) URL.revokeObjectURL(url); };
  }, [pic.storage_path]);
  return (
    <>
      <button
        type="button"
        onClick={() => blobUrl && setExpanded(true)}
        className="aspect-square rounded-xl overflow-hidden border border-white/10 bg-white/5"
      >
        {blobUrl ? <img src={blobUrl} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full animate-pulse" />}
      </button>
      {expanded && blobUrl && (
        <div
          className="fixed inset-0 z-[60] bg-black/90 flex items-center justify-center p-4"
          onClick={() => setExpanded(false)}
        >
          <img src={blobUrl} alt="" className="max-w-full max-h-full rounded-xl" />
        </div>
      )}
    </>
  );
}
